import IconButton from 'common/components/IconButton';
import { TableNavButton } from 'common/styles/TableNavButton';
import React from 'react';
import { ReactComponent as GridIcon } from 'common/icons/four-grid.svg';
import { useSearchParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Line, LineChart, RadialBar, RadialBarChart, Rectangle, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { SmallLabelCard, ProductivityContainer, RiskContainer, DashboardSmallContainer } from '../styles/DashboardStyles';
import toFixedIfNecessary from 'common/utils/toFixedIfNecessary';
import numberWithSpaces from 'common/utils/numberWithSpaces';
import styled from '@emotion/styled';
import { ReactComponent as DotsVerticalIcon } from 'common/icons/dots-vertical.svg';
import Image1 from '../images/image1.jpg';
import Image2 from '../images/image2.jpg';
import Image3 from '../images/image3.jpg';
import { ReactComponent as ClockIcon } from 'common/icons/clock-icon.svg';
import { useTheme } from '@mui/material';
import CalendarSmall from '../components/CalendarSmall';
import SelectEl from 'common/components/Select';
import CustomTooltip from '../components/CustomTooltip';

const DashboardSmallLabelCard = ({ value }: { value: number }) => {

  return (
    <SmallLabelCard negative={value < 0}>
      <span className='dashboard-small-label-text'>{(value >= 0 ? '+' : '-') + toFixedIfNecessary(Math.abs(value), 2) + '%'}</span>
    </SmallLabelCard>
  );
};

const productivityData = [
  {
    name: 'Понеділок',
    total: 3,
    gain: 24,
    loss: 16,
  },
  {
    name: 'Вівторок',
    total: 7,
    gain: 13,
    loss: 16,
  },
  {
    name: 'Середа',
    total: 8,
    gain: 98,
    loss: 78,
  },
  {
    name: 'Четвер',
    total: 1,
    gain: 39,
    loss: 11,
  },
  {
    name: 'П\'ятниця',
    total: 10,
    gain: 48,
    loss: 29,
  },
  {
    name: 'Субота',
    total: 1,
    gain: 38,
    loss: 14,
  },
  {
    name: 'Неділя',
    total: 3,
    gain: 43,
    loss: 8,
  },
];

const riskData = [
  {
    name: null,
    uv: 100,
    fill: 'transparent',
  },
  {
    name: 'Виконані',
    uv: 20,
    fill: 'rgba(249, 11, 182, 0.25)',
  },
  {
    name: 'В процессі',
    uv: 90,
    fill: '#900BF940',
  },
  {
    name: 'На перевірці',
    uv: 70,
    fill: '#1E0BF940',
  },
  {
    name: 'Протерміновані',
    uv: 30,
    fill: '#0B78F940',
  },
  {
    name: 'Не виконано',
    uv: 60,
    fill: '#0BEBF940',
  },
];

const orderData = [
  {
    name: 'Page A',
    uv: 4000,
    pv: 2400,
    amt: 2400,
  },
  {
    name: 'Page B',
    uv: 3000,
    pv: 1398,
    amt: 2210,
  },
  {
    name: 'Page C',
    uv: 2000,
    pv: 9800,
    amt: 2290,
  },
  {
    name: 'Page D',
    uv: 2780,
    pv: 3908,
    amt: 2000,
  },
  {
    name: 'Page E',
    uv: 1890,
    pv: 4800,
    amt: 2181,
  },
  {
    name: 'Page F',
    uv: 2390,
    pv: 3800,
    amt: 2500,
  },
  {
    name: 'Page G',
    uv: 3490,
    pv: 4300,
    amt: 2100,
  },
  {
    name: 'Page H',
    uv: 2490,
    pv: 6300,
    amt: 1100,
  },
];

const ClientsTopWrapper = styled.div`
    display:flex;
    flex-direction: column;
    flex-grow:1;
    flex-basis: 55%;
    position: relative;
    min-height: 420px;
`;
const ClientsTopContainer = styled.div`
    position: absolute;
    display:flex;
    gap: 16px;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;
`;
const ClientsBottomWrapper = styled.div`
    padding: 0px;
    display:flex;
    flex-direction: column;
    flex: 1;
    position: relative;
`;
const ClientsBottomContainer = styled.div`
    position: absolute;
    display:flex;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;
    padding: 16px;
    gap: 8px;
    border-radius: 16px;
    border: 1px solid ${({ theme }) => theme.colors.gray};
    background-color: ${({ theme }) => theme.colors.plainBackgroundColor};
`;

const ChronologyWrapper = styled.div`
  position: relative;
  flex: 1;
  position: relative;
`;
const ChronologyContainer = styled.div`
  position: absolute;
  display:flex;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  display:flex;
  flex-direction: column;
  gap: 20px;
`;
const ChronologyList = styled.div`
  padding-right: 10px;
  flex: 1 0 auto;
  
  padding-bottom: 29px;
  position: relative;
  &::before {
    content: '';
    position: absolute;
    bottom: 6px;
    left: 5px;
    top: 6px;
    z-index: 0;
    width: 1px;
    background-color: ${({ theme }) => theme.colors.gray + '80'};
  }
  &::after {
    content: '';
    position: absolute;
    bottom: 0px;
    left: 0px;
    height: 12px;
    width: 12px;
    border-radius: 50%;
    box-sizing: border-box;
    border: 1px solid ${({ theme }) => theme.colors.gray};
    background-color: ${({ theme }) => theme.colors.plainBackgroundColor};
  }
`;
const ChronologyScroller = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 29px;
  display:flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  padding-right: 10px;
`;

const ChronologyLineWrapper = styled.div`
  display:flex;
  gap: 12px;
  width: 100%;
`;

const ChronologyCircle = styled.div<{ color: string }>`
  height: 12px;
  width: 12px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
`;

const ChronologyLineTitle = styled.p`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 100%; /* 16px */
  letter-spacing: 1px;
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.colors.primaryColor};
  }
  &.active {
    color: ${({ theme }) => theme.colors.primaryColor};
    text-decoration: underline;
  }
`;

type ChronologyLineProps = {
  title: string;
  text: string;
  time: string;
  color: string;
};

const chronologyItems: ChronologyLineProps[] = [
  {
    title: 'Корпоратив',
    text: 'Не забудьте про вечірку',
    time: '12 хвилин тому',
    color: '#E8BCF7',
  },
  {
    title: 'Зустріч з директором',
    text: 'Зустріч з директором',
    time: '45 хвилин тому',
    color: '#F7DCBC',
  },
  {
    title: 'Зустріч з директором',
    text: 'Зустріч з директором',
    time: '10 листопада',
    color: '#BCD7F7',
  },
  {
    title: 'Зустріч з директором',
    text: 'Зустріч з директором',
    time: '21 жовтня',
    color: '#C4F7BC',
  },
  {
    title: 'Зустріч з директором',
    text: 'Зустріч з директором',
    time: '30 вересня',
    color: '#F7DCBC',
  },
  {
    title: 'Зустріч з директором',
    text: 'Зустріч з директором',
    time: '30 вересня',
    color: '#E8BCF7',
  },
  {
    title: 'Зустріч з директором',
    text: 'Зустріч з директором',
    time: '30 вересня',
    color: '#BCD7F7',
  },
  {
    title: 'Зустріч з директором',
    text: 'Зустріч з директором',
    time: '30 вересня',
    color: '#E8BCF7',
  },
];

const ChronologyLine = ({ title, text, time, color }: ChronologyLineProps) => {
  return (
    <ChronologyLineWrapper>
      <ChronologyCircle color={color} />
      <div className='dashboard-chronology-text-wrapper'>
        <ChronologyLineTitle className={title === 'Корпоратив' ? 'active' : ''}>{title}</ChronologyLineTitle>
        <span className='dashboard-chronology-text gray-text'>{text}</span>
      </div>
      <div className='dashboard-chronology-time-wrapper gray-text'>
        {time}
      </div>
    </ChronologyLineWrapper>
  );
};

const MessageWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const MessageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0px;
  display:flex;
  flex-direction: column;
  gap: 12px;
`;

const MassageContainer = styled.div`
  flex: 1 0 auto;
  position: relative;
  width: 100%;
`;

const MessageScroller = styled.div`
  padding-right: 8px;
  overflow-y: auto;
  flex: 1 0 auto;
  max-height: 300px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-left: 16px;
  border-left: 1px solid ${({ theme }) => theme.colors.gray};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const CalendarWrapper = styled.div`
  height: 100%;
  width: 523px;
  padding: 16px 24px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.gray};
  display:flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
  background-color: ${({ theme }) => theme.colors.plainBackgroundColor};
`;

const MeetingsWrapper = styled.div`
  height: 100%;
  flex: 1 0 auto;
  padding: 16px;
  padding-right: 8px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.gray};
  display:flex;
  flex-direction: column;
  gap: 24px;
  background-color: ${({ theme }) => theme.colors.plainBackgroundColor};
`;

const MeetingsList = styled.div`
  display:flex;
  flex-direction: column;
  gap: 20px;
  padding-right: 8px;
  overflow-y: auto;
`;

type MeetingItemProps = {
  image: string;
  name: string;
  time: string;
  type: string;
  typeColor: string;
};

const meetingItems: MeetingItemProps[] = [
  {
    name: 'Зустріч з директором',
    image: Image1,
    time: '08:30',
    type: 'Зустріч',
    typeColor: 'rgba(187, 11, 249, 0.25)',
  },
  {
    name: 'Мітинг із командою технологів',
    image: Image2,
    time: '12:15',
    type: 'Мітинг',
    typeColor: 'rgba(249, 140, 11, 0.25)',
  },
  {
    name: 'Обід',
    image: Image3,
    time: '13:00',
    type: 'Інше',
    typeColor: 'rgba(11, 120, 249, 0.25)',
  },
  {
    name: 'Зустріч із замовником',
    image: Image1,
    time: '15:30',
    type: 'Зустріч',
    typeColor: 'rgba(187, 11, 249, 0.25)',
  },
  {
    name: 'Корпоратив',
    image: Image2,
    time: '18:00',
    type: 'Дозвілля',
    typeColor: 'rgba(45, 249, 11, 0.25)',
  },
];
const MeetingLineWrapper = styled.div`
  display:flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
`;
const MeetingType = styled.div<{ color: string }>`
  padding: 8px 14px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background-color: ${({ color }) => color};
  font-family: Mulish;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 100%; /* 14px */
  letter-spacing: 1px;
`;
const MeetingLine = ({ image, name, time, type, typeColor }: MeetingItemProps) => {
  const theme = useTheme();
  return (
    <MeetingLineWrapper>
      <div className='dashboard-meeting-line-left'>
        <img className='dashboard-meeting-image' src={image} alt='meeting' />
        <div className='dashboard-meeting-text-wrapper'>
          <span className='dashboard-meeting-text'>{name}</span>
          <div className='dashboard-meeting-time-wrapper'>
            <ClockIcon stroke={theme.colors.gray} height={14} width={14} />
            <span className='dashboard-meeting-time gray-text'>{time}</span>
          </div>
        </div>
      </div>
      <MeetingType color={typeColor}>
        {type}
      </MeetingType>
    </MeetingLineWrapper>
  );
};

export default function DashboardEmployee() {
  const [searchParams, setSearchParams] = useSearchParams();
  const type = searchParams.get('type') || 'overview';
  const productivityDataTotal = productivityData.reduce((acc, item) => acc + item.total, 0);
  const [selectedProject, setSelectedProject] = React.useState<number | null>(0);
  const onProjectChange = (event) => {
    setSelectedProject(event.target.value);
  };
  return (
    <>
      <div className='dashboard-nav-wrapper'>
        <TableNavButton className={type === 'overview' ? 'active' : ''} onClick={() => setSearchParams({ type: 'overview' })}>Загальне</TableNavButton>
        <TableNavButton className={type === 'activity' ? 'active' : ''} onClick={() => setSearchParams({ type: 'activity' })}>Активність</TableNavButton>
      </div>
      {type !== 'activity' ? <div className='dashboard-content-wrapper'>
        <div className='dashboard-overview-top-wrapper'>
          <ProductivityContainer>
            <div className='dashboard-productivity-top-line'>
              <span className='big-text'>Продуктивність</span>
              <div className='dashboard-productivity-button-wrapper'>
                <IconButton size={36}>
                  <GridIcon className='svg-black' />
                </IconButton>
              </div>
            </div>
            <div className='dashboard-productivity-revenue-wrapper'>
              <h2 className='new-h2'>{productivityDataTotal} задачі</h2>
              <span className='dashboard-big-text'>Загальна кількість</span>
            </div>
            <div className='dashboard-graph-wrapper'>
              <div className='dashboard-graph-absolute-wrapper'>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    width={500}
                    height={300}
                    data={productivityData}
                    margin={{
                      top: 0,
                      right: 0,
                      left: 0,
                      bottom: 0,
                    }}
                    barSize={56}

                  >
                    <XAxis strokeDasharray="3 3" dataKey="name" scale="point" padding={{ left: 50, right: 50 }} />
                    <YAxis tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <CartesianGrid vertical={false} strokeDasharray="3 3" />
                    <Bar activeBar={<Rectangle fill="rgba(30, 11, 249, 1)" />} radius={[8, 8, 0, 0]} dataKey={'total'} fill="rgba(30, 11, 249, 0.50)" background={{ fill: 'rgba(155, 158, 171, 0.15)', radius: '8px 8px 0 0' }} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </ProductivityContainer>
          <RiskContainer>
            <span className='big-text'>Показник ризиків</span>
            <span style={{ marginTop: '4px' }} className='dashboard-big-text'>Замовлення</span>
            <div style={{ maxHeight: '500px' }} className='dashboard-graph-wrapper'>
              <div className='dashboard-graph-absolute-wrapper'>
                <ResponsiveContainer width="100%" height="100%">
                  <RadialBarChart startAngle={360 + 90} endAngle={90} innerRadius="15%" outerRadius="100%" barSize={15} data={riskData}>
                    <RadialBar
                      label={false}
                      background={false}
                      dataKey="uv"
                      maxBarSize={100}
                      cornerRadius={15}
                    />
                  </RadialBarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className='dashboard-legend-wrapper'>
              {riskData.map((item, index) => {
                if (item.name === null) return null;
                return (
                  <div key={index} className='dashboard-legend-line'>
                    <div className='dashboard-legend-line-left'>
                      <div style={{ backgroundColor: item.fill }} className='dashboard-legend-color'></div>
                      <span className='dashboard-legend-text'>{item.name}</span>
                    </div>
                    <span className='dashboard-legend-text'>{item.uv}</span>
                  </div>
                );
              })}
            </div>
          </RiskContainer>
        </div>
        <div className='dashboard-overview-bottom-wrapper'>
          <DashboardSmallContainer>
            <div className='dashboard-overview-bottom-absolute-wrapper'>
              <div className='dashboard-small-top-line'>
                <div className='dashboard-small-title-line'>
                  <h2 className='new-h2'>{productivityDataTotal / 2} ₴</h2>
                  <div className='dashboard-small-label-wrapper'>
                    <DashboardSmallLabelCard value={-2.51} />
                    <IconButton size={32}>
                      <GridIcon className='svg-black' />
                    </IconButton>
                  </div>
                </div>
                <span className='dashboard-big-text'>Загальний дохід</span>
              </div>
              <div className='dashboard-graph-wrapper'>
                <div className='dashboard-graph-absolute-wrapper'>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      width={300}
                      height={300}
                      data={productivityData}
                      margin={{
                        top: 0,
                        right: 0,
                        left: 0,
                        bottom: 0,
                      }}
                      barSize={22}

                    >
                      <Bar radius={[8, 8, 0, 0]} dataKey="loss" fill="#0BEBF940" background={{ fill: 'transparent' }} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </DashboardSmallContainer>
          <DashboardSmallContainer>
            <div className='dashboard-overview-bottom-absolute-wrapper'>
              <div className='dashboard-small-top-line'>
                <div className='dashboard-small-title-line'>
                  <h2 className='new-h2'>{numberWithSpaces(15548)}₴</h2>
                  <DashboardSmallLabelCard value={6} />
                </div>
                <span className='dashboard-big-text'>Очікувана зарплатня</span>
              </div>
              <div className='dashboard-graph-wrapper'>
                <div className='dashboard-graph-absolute-wrapper'>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart width={300} height={100} data={orderData}>
                      <Line type="monotone" dot={{ fill: '#26DA2D66', height: 4, r: 2, width: 4 }} dataKey="pv" stroke="#26DA2D66" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </DashboardSmallContainer>
          <DashboardSmallContainer>
            <div className='dashboard-overview-bottom-absolute-wrapper'>
              <div className='dashboard-small-top-line'>
                <div className='dashboard-small-title-line'>
                  <h2 className='new-h2'>15 листопада 2023</h2>

                </div>
                <span className='dashboard-big-text'>Дата видачі зарплатні</span>
              </div>
              <div className='dashboard-salary-wrapper'>
                <div className='dashboard-salary-line'>
                  <span className='dashboard-salary-text'>Жовтень</span>
                  <span className='dashboard-salary-text'>15 (п’ятниця)</span>
                </div>
                <div className='dashboard-salary-line'>
                  <span className='dashboard-salary-text'>Вересень</span>
                  <span className='dashboard-salary-text'>12 (понеділок)</span>
                </div>
                <div className='dashboard-salary-line'>
                  <span className='dashboard-salary-text'>Серпень</span>
                  <span className='dashboard-salary-text'>18 (п’ятниця)</span>
                </div>
                <div className='dashboard-salary-line'>
                  <span className='dashboard-salary-text'>Липень</span>
                  <span className='dashboard-salary-text'>14 (вівторок)</span>
                </div>
              </div>
            </div>
          </DashboardSmallContainer>
        </div>
      </div> : <div className='dashboard-content-wrapper'>
        <ClientsTopWrapper>
          <ClientsTopContainer>
            <CalendarWrapper>
              <CalendarSmall />
            </CalendarWrapper>
            <MeetingsWrapper>
              <div className='dashboard-meetings-line'>
                <span className='big-text'>
                  Розклад
                </span>
                <DotsVerticalIcon className='svg-black' style={{
                  cursor: 'pointer',
                }} />
              </div>
              <MeetingsList className='u-fancy-scrollbar'>
                {meetingItems.map((item, index) => <MeetingLine key={index} {...item} />)}
              </MeetingsList>
            </MeetingsWrapper>
          </ClientsTopContainer>
        </ClientsTopWrapper>
        <ClientsBottomWrapper>
          <ClientsBottomContainer>
            <ChronologyWrapper>
              <ChronologyContainer>
                <span className='big-text'>Хроногія активностей</span>
                <ChronologyList>
                  <ChronologyScroller className='u-fancy-scrollbar'>
                    {chronologyItems.map((item, index) => <ChronologyLine key={index} {...item} />)}
                  </ChronologyScroller>
                </ChronologyList>
              </ChronologyContainer>
            </ChronologyWrapper>
            <MessageWrapper>
              <MessageContainer>
                <div className='dashboard-message-top-line'>
                  <span className='dashboard-message-top-line-text'>
                    Проект:
                  </span>
                  <SelectEl
                    placeholder='Оберіть проект'
                    data={[
                      {
                        label: 'Проект 1',
                        value: 1,
                      },
                      {
                        label: 'Проект 2',
                        value: 2,
                      },
                      {
                        label: 'Проект 3',
                        value: 3,
                      },
                    ]}
                    value={selectedProject}
                    onChange={onProjectChange}
                  />
                  <DotsVerticalIcon className='svg-black' style={{
                    cursor: 'pointer',
                  }} />
                </div>
                <MassageContainer>
                <MessageScroller className='u-fancy-scrollbar'>
                  <div className='dashboard-message-top'>
                    <p className='new-p'>Шановні колеги!</p>
                    <span className='dashboard-message-time gray-text'>15 листопада 2023</span>
                  </div>
                  <span
                    className='dashboard-rating-text gray-text'
                    style={{
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {`Не забудьте про вечірку!
                  \n
                  З радістю запрошуємо вас взяти участь у щорічному корпоративному заході компанії KIMS.
                  \n
                  Він відбудеться 15 листопада у ресторані “Риба” о 7 вечора.
                  \n
                  Це буде вечір повний веселощів, незабутніх вражень та можливості поділитися радістю успіхів, досягнень та теплих моментів року.
                  \n
                  З нетерпінням чекаємо на вас на корпоративі!

                  З повагою, Сергію!`}
                  </span>
                </MessageScroller>
                </MassageContainer>
              </MessageContainer>
            </MessageWrapper>
          </ClientsBottomContainer>
        </ClientsBottomWrapper>
      </div>}
    </>
  );
}